import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Modal } from "@/components/ui/modal";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { StatusPill } from "@/components/ui/status-pill";
import { toast } from "@/components/ui/sonner";

import {
  useBackfillStatus,
  useBindPromoCode,
  useConfirmBackfill,
  usePromoCodeGroupsQuery,
  usePromoCodesQuery,
  useStartBackfill,
} from "../api";
import { apiErrorDetail } from "../lib/errors";
import type { PromoCode } from "../types";

/**
 * Admin screen for Stripe promotion codes and the partner groups that claim them.
 *
 * Mirrors the Django screen at `/administration/codes-promotionnels/`, over the
 * same service: both apps list, filter, bind and backfill through
 * `accounts.promo_codes` and `accounts.backfill`. Keeping the rules in one place
 * is the point — a SPA admin surface with its own rules is the failure mode
 * FINDINGS records for the tools back-office.
 *
 * Follows the expert admin list's filter shape (per-facet state fed to the
 * query) rather than SuperUserManagement, which has no search at all.
 */
export function PromoCodeManagement() {
  const { t } = useTranslation();

  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const [group, setGroup] = useState("");

  const params: Record<string, string> = {};
  if (q) params.q = q;
  if (state) params.state = state;
  if (group) params.group = group;

  const codes = usePromoCodesQuery(params);

  // Only groups that actually claim a code, matching the Django filter form —
  // offering every group would be a list of options that can never match a row.
  // Derived from the unfiltered list, not the displayed one, so choosing a group
  // does not collapse the picker to that single option. With no filter applied
  // both queries share a cache key, so this is one request, not two.
  const allCodes = usePromoCodesQuery({});
  const claimingGroups = useMemo(() => {
    const seen = new Map<string, string>();
    for (const row of allCodes.data ?? []) {
      if (row.group_id && row.group_name) seen.set(row.group_id, row.group_name);
    }
    return [...seen]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allCodes.data]);

  const [bindTarget, setBindTarget] = useState<PromoCode | null>(null);
  const [backfill, setBackfill] = useState<{ groupId: string; name: string } | null>(
    null,
  );

  // The scan starts here, on the click — not from an effect inside the modal.
  // StrictMode mounts, unmounts and remounts a component in development, and a
  // mutation fired from a mount effect loses its result to that cycle: the scan
  // ran server-side while the modal sat on "analysing" forever. An event handler
  // is not double-invoked, so the token always lands.
  const startScan = useStartBackfill();
  const openBackfill = (row: PromoCode) => {
    startScan.reset();
    setBackfill({ groupId: row.group_id as string, name: row.group_name ?? "" });
    startScan.mutate(row.group_id as string);
  };


  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("promoCodes.title")}</CardTitle>
          <CardDescription>{t("promoCodes.intro")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="promo-q">{t("promoCodes.search")}</Label>
              <Input
                id="promo-q"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("promoCodes.searchPlaceholder")}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="promo-state">{t("promoCodes.state")}</Label>
              <select
                id="promo-state"
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="flex min-h-touch w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">{t("promoCodes.stateAll")}</option>
                <option value="bound">{t("promoCodes.stateBound")}</option>
                <option value="unbound">{t("promoCodes.stateUnbound")}</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="promo-group">{t("promoCodes.group")}</Label>
              <SearchableSelect
                id="promo-group"
                value={group}
                onChange={setGroup}
                options={claimingGroups}
                placeholder={t("promoCodes.groupAll")}
                searchPlaceholder={t("promoCodes.groupSearch")}
                emptyLabel={t("promoCodes.noGroupMatch")}
              />
            </div>
          </div>

          {q || state || group ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setQ("");
                setState("");
                setGroup("");
              }}
            >
              {t("promoCodes.reset")}
            </Button>
          ) : null}

          {codes.isPending ? (
            <p className="text-sm text-muted-foreground">{t("promoCodes.loading")}</p>
          ) : codes.isError ? (
            <p className="text-sm text-destructive">
              {apiErrorDetail(codes.error) ?? t("promoCodes.loadError")}
            </p>
          ) : (
            <PromoCodeTable
              rows={codes.data ?? []}
              onBind={setBindTarget}
              onBackfill={openBackfill}
            />
          )}
        </CardContent>
      </Card>

      {bindTarget ? (
        <BindModal code={bindTarget} onClose={() => setBindTarget(null)} />
      ) : null}
      {backfill ? (
        <BackfillModal
          groupName={backfill.name}
          token={startScan.data?.token ?? null}
          startError={startScan.error}
          onClose={() => setBackfill(null)}
        />
      ) : null}
    </div>
  );
}

function discountLabel(row: PromoCode) {
  if (row.percent_off) return `${row.percent_off} %`;
  if (row.amount_off) return `${(row.amount_off / 100).toFixed(2)} $`;
  return "—";
}

function PromoCodeTable({
  rows,
  onBind,
  onBackfill,
}: {
  rows: PromoCode[];
  onBind: (row: PromoCode) => void;
  onBackfill: (row: PromoCode) => void;
}) {
  const { t } = useTranslation();

  if (!rows.length) {
    return <p className="text-sm text-muted-foreground">{t("promoCodes.empty")}</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b text-muted-foreground">
            <th className="py-2 pr-3 font-medium">{t("promoCodes.colCode")}</th>
            <th className="py-2 pr-3 font-medium">{t("promoCodes.colDiscount")}</th>
            <th className="py-2 pr-3 font-medium">{t("promoCodes.colUses")}</th>
            <th className="py-2 pr-3 font-medium">{t("promoCodes.colState")}</th>
            <th className="py-2 pr-3 font-medium">{t("promoCodes.colGroup")}</th>
            <th className="py-2 font-medium">{t("promoCodes.colActions")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.code} className="border-b last:border-0">
              <td className="py-2 pr-3 font-mono">{row.code}</td>
              <td className="py-2 pr-3 tabular-nums">{discountLabel(row)}</td>
              <td className="py-2 pr-3 tabular-nums">{row.times_redeemed}</td>
              <td className="py-2 pr-3">
                <StatusPill tone={row.group_id ? "success" : "warning"}>
                  {row.group_id ? t("promoCodes.bound") : t("promoCodes.unbound")}
                </StatusPill>
              </td>
              <td className="py-2 pr-3">{row.group_name ?? "—"}</td>
              <td className="py-2">
                {row.group_id ? (
                  <Button variant="outline" size="sm" onClick={() => onBackfill(row)}>
                    {t("promoCodes.backfill")}
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => onBind(row)}>
                    {t("promoCodes.bind")}
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BindModal({ code, onClose }: { code: PromoCode; onClose: () => void }) {
  const { t } = useTranslation();
  const groups = usePromoCodeGroupsQuery();
  const bind = useBindPromoCode();
  const [groupId, setGroupId] = useState("");

  const submit = () => {
    if (!groupId) return;
    bind.mutate(
      { code: code.code, partner_group: groupId },
      {
        onSuccess: () => {
          toast.success(t("promoCodes.bindSucceeded", { code: code.code }));
          onClose();
        },
        onError: (err) =>
          toast.error(apiErrorDetail(err) ?? t("promoCodes.bindFailed")),
      },
    );
  };

  return (
    <Modal onClose={onClose} title={t("promoCodes.bindTitle")}>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">{t("promoCodes.bindHint")}</p>
        <div className="space-y-1.5">
          <Label>{t("promoCodes.colCode")}</Label>
          <Input value={code.code} readOnly />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="bind-group">{t("promoCodes.colGroup")}</Label>
          <SearchableSelect
            id="bind-group"
            value={groupId}
            onChange={setGroupId}
            options={groups.data ?? []}
            placeholder={t("promoCodes.chooseGroup")}
            searchPlaceholder={t("promoCodes.groupSearch")}
            emptyLabel={t("promoCodes.noGroupMatch")}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t("promoCodes.cancel")}
          </Button>
          <Button onClick={submit} disabled={!groupId || bind.isPending}>
            {t("promoCodes.bind")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Preview a group's missing memberships and apply the ones left ticked.
 *
 * The scan runs in a task, so this starts it and then polls. Everything starts
 * selected: the common case is accepting the whole reconstruction, and unticking
 * is the exception.
 */
function BackfillModal({
  groupName,
  token,
  startError,
  onClose,
}: {
  groupName: string;
  /** From the scan the caller started; null until it answers. */
  token: string | null;
  startError: unknown;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const confirm = useConfirmBackfill();
  const [deselected, setDeselected] = useState<Set<string>>(new Set());

  const status = useBackfillStatus(token);

  const ready = status.data?.state === "ready";
  const candidates = ready ? (status.data?.candidates ?? []) : [];
  const selected = candidates
    .map((c) => c.email)
    .filter((email) => !deselected.has(email));

  const toggle = (email: string) => {
    setDeselected((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const submit = () => {
    if (!token || !selected.length) return;
    confirm.mutate(
      { token, emails: selected },
      {
        onSuccess: (data) => {
          toast.success(
            t("promoCodes.joined", { count: data.joined, group: groupName }),
          );
          if (data.over_cap) toast.warning(t("promoCodes.overCapWarning"));
          // One per refusal, as Django queues one message each; the reason
          // arrives already translated.
          data.failed.forEach(({ email, reason }) =>
            toast.error(t("promoCodes.memberFailed", { email, reason })),
          );
          onClose();
        },
        onError: (err) =>
          toast.error(apiErrorDetail(err) ?? t("promoCodes.confirmFailed")),
      },
    );
  };

  return (
    <Modal
      onClose={onClose}
      title={`${t("promoCodes.backfillTitle")} — ${groupName}`}
    >
      {startError || status.isError ? (
        <p className="text-sm text-destructive">
          {apiErrorDetail(startError ?? status.error) ?? t("promoCodes.scanFailed")}
        </p>
      ) : !ready ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          {t("promoCodes.scanning", {
            scanned: status.data?.invoices_scanned ?? 0,
          })}
        </p>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t("promoCodes.scanned", {
              count: status.data?.invoices_scanned ?? 0,
            })}
          </p>

          {candidates.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="py-2 pr-3 font-medium">
                      {t("promoCodes.colMember")}
                    </th>
                    <th className="py-2 pr-3 font-medium">{t("promoCodes.colCode")}</th>
                    <th className="py-2 pr-3 font-medium">
                      {t("promoCodes.colSubscription")}
                    </th>
                    <th className="py-2 font-medium">{t("promoCodes.colLicences")}</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((c) => (
                    <tr key={c.email} className="border-b last:border-0">
                      <td className="py-2 pr-3">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={!deselected.has(c.email)}
                            onChange={() => toggle(c.email)}
                          />
                          {c.email}
                        </label>
                      </td>
                      <td className="py-2 pr-3 font-mono text-xs">{c.matched_code}</td>
                      <td className="py-2 pr-3">
                        {/* A payload cached by an older worker has no key at all;
                            show "—" for it, as for "none", never a raw i18n key. */}
                        {!c.subscription_state || c.subscription_state === "none"
                          ? "—"
                          : t(`promoCodes.subscriptionState.${c.subscription_state}`)}
                      </td>
                      <td className="py-2 tabular-nums">
                        {c.licences_before} → {c.licences_after}
                        {c.max_licences ? ` / ${c.max_licences}` : ""}
                        {c.over_cap ? (
                          <span className="ml-2">
                            <StatusPill tone="warning">
                              {t("promoCodes.overCap")}
                            </StatusPill>
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t("promoCodes.noCandidates")}</p>
          )}

          {/* The buckets that must be stated rather than dropped. */}
          <Buckets status={status.data} />

          <p className="text-xs text-muted-foreground">
            {t("promoCodes.neverSubscribed")}
          </p>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              {t("promoCodes.cancel")}
            </Button>
            <Button
              onClick={submit}
              disabled={!selected.length || confirm.isPending}
            >
              {t("promoCodes.addSelected", { count: selected.length })}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function Buckets({ status }: { status?: BackfillStatusLike }) {
  const { t } = useTranslation();
  if (!status) return null;

  const sections: { key: string; label: string; items: string[] }[] = [
    {
      key: "skipped",
      label: t("promoCodes.bucketSkipped"),
      items: (status.skipped ?? []).map(
        (s) => `${s.email} — ${t("promoCodes.alreadyIn", { group: s.current })}`,
      ),
    },
    {
      key: "unresolved",
      label: t("promoCodes.bucketUnresolved"),
      items: status.unresolved_bindings ?? [],
    },
  ];

  return (
    <>
      {sections
        .filter((section) => section.items.length)
        .map((section) => (
          <details key={section.key} className="text-sm">
            <summary className="cursor-pointer text-muted-foreground">
              {section.label} ({section.items.length})
            </summary>
            <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
              {section.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </details>
        ))}
    </>
  );
}

/** The bits of the status payload the buckets read. */
type BackfillStatusLike = {
  skipped?: { email: string; current: string }[];
  unresolved_bindings?: string[];
};
